import { z } from 'zod';
import { boardSchema } from './board';
import { boardBackgroundSchema } from './board-background';
import { cardAgingFieldsSchema, cardAgingModeSchema } from './card-aging';
import { cardSchema } from './card';
import { cardFrontApprovalsSchema } from './card-approval';
import { cardCoverSchema } from './card-cover';
import { cardCustomFieldBadgeSchema } from './custom-field';
import { ulidSchema } from './id';
import { cardLabelSchema, labelListSchema } from './label';
import { listSchema } from './list';
import { placedStickerSchema } from './sticker';
import { publicProfileSchema } from './user';

export const LINK_CARD_TARGET_URL_MAX_LENGTH = 2048;
export const LINK_CARD_TARGET_TITLE_CACHE_MAX_LENGTH = 512;
export const LINK_CARD_TARGET_BOARD_TITLE_CACHE_MAX_LENGTH = 512;

/**
 * The board face in one payload — the contract layer for block **B13**.
 *
 * `BE-13.1` (`FR-VIEW-007`): "The board face renders lists left to right in
 * board order, each holding its cards in list order, each list showing its name
 * and card count."
 *
 * ---------------------------------------------------------------------------
 * THIS FILE COMPOSES THE THREE CONTRACTS ABOVE IT. IT RESTATES NONE OF THEM,
 * AND THAT IS THE WHOLE DESIGN
 * ---------------------------------------------------------------------------
 * `./board` (B08/B09/B10), `./list` (B11) and `./card` (B12) already say what a
 * board, a list and a card are on the wire, and each of them is the response
 * serialiser of routes that are already shipped. So this file imports all three
 * and adds exactly **two** fields: `cards` and `openCardCount`, both on the
 * list.
 *
 * The rejected shape was a self-contained `BoardFace` declaring its own board,
 * list and card views. It reads better in isolation and it is wrong in a way
 * that is invisible for exactly as long as nothing changes: `GET /boards/:id`
 * and `GET /boards/:id/contents` would then answer with **two** board shapes
 * that a reader has to diff by eye, and the day `BE-38.5` or `BE-58.x` adds a
 * field to one of them, a client rendering the board face would see it on the
 * header read and not on the board read — with no compiler, no schema and no
 * test anywhere to notice. Composition makes that class of drift
 * unrepresentable: there is one `cardSchema` in this package and every route
 * that returns a card returns *it*.
 *
 * ⚠ **`listSchema` itself is not widened, and `.extend()` on a copy is the
 * mechanism.** `listSchema` is the response serialiser of all six of B11's
 * body-bearing routes; adding `cards` to it would put a card array on
 * `POST /lists`, on `PATCH /lists/:id/title` and — worst — on
 * `GET /boards/:id/lists`, which four docstrings say must never become a card
 * read. `boardFaceListSchema` is a *derived* view used by this one route, and
 * B11's contract is untouched by it.
 *
 * ---------------------------------------------------------------------------
 * AND THIS IS STILL NOT `BE-02.1`
 * ---------------------------------------------------------------------------
 * `BE-02.1` — the full Phase 1 object model (board, list, card, comment, label,
 * notification) — is a different item and stays PENDING. `./list` and `./card`
 * each recorded that about themselves and it holds here with a twist worth
 * saying out loud, because this file is the first one that could be mistaken
 * for it: an aggregate of three narrow views is still narrow. What crosses the
 * wire below is precisely what `./board`, `./list` and `./card` already carry,
 * so every field `BE-02.1` would add — a comment, a label, a card's
 * description — is absent here for the reason it is absent *there*, and adding
 * one to this file would be adding it to the three, sideways and without the
 * route that needs it.
 *
 * ⚠ **The card front's badges are deliberately absent, and their absence is a
 * scope boundary rather than an oversight.** `FR-VIEW-007`'s behaviour text
 * ends "Card fronts show the badges defined in `02-card-detail.md` — cover,
 * labels, due date, members, checklist and attachment counts", and **not one of
 * those six is on `cardSchema`**. Each has an owner: labels `BE-15.x`
 * (`FR-CARD-009`), members and the description/comment badges `BE-25.1`
 * (`FR-BOARD-030`), dates `BE-24.x` (`FR-CARD-011`–`FR-CARD-014`), the cover
 * itself `BE-25.1`/`BE-25.2` (`FR-CARD-021`, `FR-BOARD-031`), ~~checklists
 * `BE-26.x`~~ (landed — see the B26 entry below), ~~attachments `BE-27.x`~~
 * (landed — see the B27 entry below, and it is the sixth and last: every
 * badge `FR-VIEW-007` names now lives on `boardFaceCardSchema`).
 * ⚠ **"members `BE-14.3`" stood here and is
 * corrected in place: B14 put a card's members on the CARD BACK
 * (`cardDetailSchema.members` in `./card-detail`) and never on the board face
 * — no backend item anywhere before `BE-25.1` projected `card_members` onto
 * this shape, and the misattribution is fixed at the point a reader would
 * otherwise go looking for it in the wrong block.** The block that builds
 * each of them widens `cardSchema` or extends `boardFaceCardSchema` on a copy
 * and this route inherits or gains the field accordingly — which is the
 * second dividend of composing rather than restating, and the reason a
 * reader who greps `FR-VIEW-007` and finds no `labels` here should conclude
 * "not yet mine" rather than "missing".
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE PARAGRAPH ABOVE PREDICTED THE MECHANISM AND THE FIRST BADGE BLOCK
 * CHOSE THE OTHER ONE. CORRECTED IN PLACE, NOT DELETED
 * ---------------------------------------------------------------------------
 * **B15 (labels, `FR-CARD-009`) is the first of those six to land, and it did
 * not widen `cardSchema`.** It declared `cardLabelSchema` in `./label` and this
 * file extends on a **copy** — `boardFaceCardSchema` below — exactly as
 * `boardFaceListSchema` already extends `listSchema`. So `labels` is no longer
 * "not yet mine": it is on the board face, one level in, and `cardSchema` is
 * byte-identical to what it was.
 *
 * The fact that decides nothing and must be stated so the choice is not read as
 * the conservative one: **both paths change a shipped response.** Widening
 * `cardSchema` would have changed B12's four card routes; extending on a copy
 * changes this one — `cards` below is now `z.array(boardFaceCardSchema)` rather
 * than `z.array(cardSchema)`, and `GET /boards/:id/contents` is shipped. What
 * tipped it is that a label belongs on the surfaces rendering cards *in
 * context* (this one, and the card back) and on none of the four answering
 * "what is this card now" — `POST /cards/:id/move` would acquire a label read
 * on the drag path for a field the drag does not touch.
 *
 * ⚠ **Two costs come with it and neither is free.** The first is the splice
 * invariant `cards`' own docstring states by name — see it below, where the
 * correction is made a second time at the point a client would feel it. The
 * second is that the compile-time net the README describes for this file's four
 * local mappers ("every mapper here is annotated `Board`, `List` or `Card` from
 * `@molt/shared`, so a field added to any of those is a compile error in this
 * file until it is projected and mapped") **does not fire for `labels`**,
 * because the field is not on `cardSchema`. It has to be re-armed by hand: the
 * board-face card mapper in `apps/server/src/routes/board-contents.ts` is
 * annotated `BoardFaceCard` rather than `Card`. Leave it as `Card` and the
 * failure moves to a confusing excess-property error at the call site; remove
 * the annotation and there is no failure at all.
 *
 * A later badge block (dates, `BE-24.x`; cover, `BE-25.x`) should read this
 * entry rather than the prediction above it, and weigh the trade again: the
 * dividend of widening `cardSchema` is real and is preserved for any field
 * every route serving a card should carry.
 *
 * ⚠ **B24 stage 1 has now landed and it weighed the trade the same way B15
 * did.** `startAt` and `dueAt` are added to `boardFaceCardSchema` below, on a
 * copy, because `FR-CARD-013` itself names the card front's date badge —
 * `cardSchema` stays byte-identical to what B12 shipped, and B12's four
 * routes still serve no dates. `cardDetailSchema` in `./card-detail` carries
 * a third field this shape does not, `dueReminderMinutesBefore` — the split
 * is the same "surfaces that render a card in context" argument, narrowed
 * further, because a reminder offset has no card-front rendering at all.
 *
 * ⚠ **B24 stage 2 adds a FOURTH field, `completed`, and it went `startAt`/
 * `dueAt`'s way rather than `dueReminderMinutesBefore`'s.** `FR-CARD-004`'s
 * behaviour text names a card-front control explicitly — "a completion
 * toggle … appears on hover on the card front" — and AC1 requires the green
 * check on "both card front and card back", so `completed` belongs here on
 * the identical footing to the two dates, not the reminder offset's. `cardSchema`
 * stays exactly as B12 shipped it; B12's four routes still serve no
 * completion state.
 *
 * ⚠ **B25 stage 1 (`BE-25.1`, `FR-BOARD-030`) adds FOUR more fields — the
 * badge strip itself — on the identical footing: extend on a copy, never
 * widen `cardSchema`.** `hasDescription`, `commentCount`, `members` and
 * `cover` are added below. `hasDescription` and `cover` cost nothing extra
 * to read — both come off columns (or a derived boolean over one) on the row
 * the card statement already selects. `commentCount` and `members` are each
 * a **second statement**, on `labels`' own precedent one level up
 * (`boardContentsSchema.labels`/`boardFaceCardSchema.labels` are already two
 * statements for one badge), grouped by card id in memory rather than
 * joined into the card statement itself — a join would fan a card with
 * three comments and two members out to six rows, and `count(*)` over that
 * join would report the wrong number for whichever badge lost the
 * cardinality race.
 *
 * ⚠ **`members` here is `publicProfileSchema`, not `cardMemberSchema`.**
 * `cardDetailSchema.members` (`./card-detail`) carries `joinedAt` because
 * the card back is a chronological record of who joined and when; the board
 * face's avatar row needs none of that, only "whose face to draw", so this
 * shape reuses `publicProfileSchema` directly rather than declaring a
 * fourth member view beside `cardMemberSchema`, `boardMemberSchema` and
 * `workspaceMemberSchema` — `cardLabelSchema`'s board-scale-narrowing
 * precedent, applied one table over.
 *
 * ⚠⚠ **B26 adds the checklist badge — `checklistItemCount` and
 * `checklistCheckedItemCount` — and it is the FIRST badge on this shape that
 * is a SUMMARY OF A COLLECTION THE CARD BACK SHIPS WHOLE.** `FR-CARD-018`'s
 * AC4 is the authority and it is unusually literal: "given a card with two
 * checklists totalling 7 items of which 3 are checked, when the board is
 * viewed, then the card front shows a checklist badge of 3/7." Two integers
 * summed across **all** of a card's checklists, on a copy, `commentCount`'s
 * footing exactly — a second statement grouped by card id in the route, never
 * a join into the card statement, because a card with three labels and two
 * checklists would otherwise multiply rows before `count(*)` saw them.
 *
 * ⚠ **`checklists` itself — the array `cardDetailSchema` carries — is
 * deliberately NOT here, and this is the sharpest instance of that rule the
 * shape has had.** A checklist's items are unbounded in principle
 * (`FR-CARD-016`: "Trello documents no limit on checklists per card", and
 * `FR-CARD-017` none on items), so a board face carrying them would be
 * `description`'s 16 KB-per-card problem with no cap at all — on the one
 * response that scales with the size of the board. The badge is a *summary
 * that exists because the card back is not open* — `commentCount`'s
 * argument, and `cardDetailSchema.cover`'s docstring makes the same one from
 * the other side.
 *
 * ⚠ **`cover` is `./card-cover`'s `cardCoverSchema`, nullable — the VIEW half
 * only.** `BE-25.1` is a read: nothing here validates a cover on the way in,
 * because nothing in this stage writes one. Every card in the product is
 * `cover: null` until `BE-25.2` ships the write routes, and this field's job
 * today is to carry that fact rather than to omit it — `FR-BOARD-030` names
 * `cover` as one of the four badge fields, so it belongs on this shape now,
 * on the same reasoning `startAt`/`dueAt` were added a badge block ahead of
 * their own reminder offset.
 *
 * ⚠⚠ **B27 adds `attachmentCount` — the SIXTH of `FR-VIEW-007`'s six named
 * badges, and the LAST: nothing remains unbuilt on this shape after it.**
 * `FR-CARD-022` AC4: "given a card has attachments, when the board is
 * viewed, then the card front shows an attachment count badge." One integer,
 * `commentCount`'s footing exactly — a card's attachments are read by count
 * alone, never by array, for the identical scaling reason `checklists` stops
 * at the card back.
 *
 * ⚠ **It counts BOTH kinds, file and link, and that is argued at the field
 * itself rather than assumed from AC4's placement in the file-upload
 * requirement.** `FR-CARD-024` — the requirement that owns the badge's own
 * *behaviour*, including the decrement-on-delete edge case — treats every
 * attachment uniformly and names no kind. See `attachmentCount`'s own
 * docstring below for the argument in full; it is not repeated here because
 * this header is where a reader learns *that* a decision was made, and the
 * field is where they learn *what* it was.
 *
 * The file needs no input primitives at all: the route it serves is a **read**
 * with a path parameter and a strictly empty querystring, so there is no body
 * to validate and nothing to normalise. It is therefore all view, which is why
 * the input/view division every other file in this package opens with is stated
 * here only to say it does not apply.
 */

// ---------------------------------------------------------------------------
// Views — what the route RETURNS. There is no input half; see the header.
// ---------------------------------------------------------------------------

/**
 * A card as the **board face** serves it: `cardSchema` plus its labels
 * (B15) and, since B24 stage 1, its start and due dates.
 *
 * `cardSchema.extend()` on a **copy**, added by B15 (`FR-CARD-009` AC2: "given
 * a card carries three labels, when the board is viewed, then all three render
 * on the card front in the board's label set order"). The header argues the
 * general case at length; three things belong here, at the shape itself.
 *
 * ⚠ **`cardSchema` is NOT widened**, so `POST /cards`, `GET /lists/:id/cards`,
 * `PATCH /cards/:id/title` and `POST /cards/:id/move` are byte-identical to
 * what B12 shipped and none of them performs a label read. The card back gets
 * the same field the same way — `cardDetailSchema` in `./card-detail` — so the
 * two surfaces that render a card in context both carry it and the four that
 * answer "what is this card now" do not.
 *
 * ⚠ **The array is ordered by the BOARD'S SET ORDER, not by application
 * time.** `FR-CARD-009` AC2's "in the board's label set order" is the whole of
 * what fixes it: the join is `card_labels → labels` with
 * `ORDER BY labels.position, labels.id`, never `card_labels.created_at`. A
 * handler that sorts by application time passes every "the three labels are
 * there" assertion while violating the only sentence that says what order means
 * here, and the card front then disagrees with the card back about one card
 * with nothing failing.
 *
 * ⚠ **Colourless labels are present in this array.** `FR-CARD-009` says they
 * "render only on the card back" and AC4 makes it observable; that suppression
 * is `FE-15.2`'s and there is no `WHERE colour <> 'colourless'` anywhere on the
 * server. Filtering here would make this route and `GET /cards/:id` return
 * different `labels` for one card with no status code to say so, and would
 * break `FR-CARD-012`/`FR-SEARCH-003` by construction. `LABEL_COLOURS` in
 * `./label` carries the argument.
 *
 * ⚠ **This shape exists because of a trade, and `BoardFaceCard` is the name the
 * server's mapper must be annotated with** — see the header's second cost.
 *
 * ⚠ **B24 stage 1 (`FR-CARD-013`) widened it a second time, on the identical
 * trade B15 made and for the identical reason.** `startAt` and `dueAt` are
 * added below, on a copy, rather than by widening `cardSchema` — `FR-CARD-013`
 * itself names the card front's date badge, so the board face genuinely wants
 * these two fields, exactly as it wants `labels`. `cardDetailSchema` in
 * `./card-detail` carries a **third** field, `dueReminderMinutesBefore`, that
 * this shape deliberately does not: no requirement puts the reminder offset on
 * a card front, so it stops at the card back.
 *
 * ⚠ **B24 stage 2 (`FR-CARD-004`) widened it a third time, on `startAt`/
 * `dueAt`'s footing rather than `dueReminderMinutesBefore`'s.** `completed`
 * is added below for the reason the module header now states: the
 * requirement's own behaviour text puts a completion control on the card
 * front, not only the card back, and AC1 requires the green check on both.
 * `cardDetailSchema` now carries a **fourth** field beyond this shape's
 * three — still `dueReminderMinutesBefore`, still card-back-only, for the
 * identical reason it always was.
 *
 * ⚠ **B26 (`FR-CARD-018`) widened it again — two integers, and they are
 * the first fields here that SUMMARISE something the card back ships in
 * full.** `checklistItemCount` and `checklistCheckedItemCount` are added
 * below; `cardDetailSchema.checklists` (the whole array, every item of
 * every checklist) stays card-back-only, which is the widest gap between
 * the two shapes there has ever been and is exactly the gap the badge
 * exists to close.
 *
 * ⚠ **B27 (`FR-CARD-022` AC4) widens it a sixth and final time — one
 * integer, `attachmentCount`, on `commentCount`'s footing rather than the
 * checklist pair's.** A card's attachments have no second number the way a
 * checklist has a numerator and a denominator. The array itself —
 * `attachmentListSchema` from `./attachment.ts`, planned for
 * `cardDetailSchema` on `checklists`' precedent (the whole set client-side
 * for `FR-CARD-024`'s next/previous preview navigation, which a paged read
 * cannot serve at a page edge) — is deliberately **not** projected onto this
 * shape either, for the identical unbounded-collection reason `checklists`
 * is not. That widening is a different owed item from this one (this file's
 * concern stops at `boardFaceCardSchema`) and is `card-detail.ts`'s to make.
 *
 * ⚠ **"A SIXTH AND FINAL TIME" WAS TRUE OF `FR-VIEW-007`'S SIX NAMED BADGES
 * AND IS NOT TRUE OF THIS SHAPE.** It has been widened three times since —
 * `customFieldBadges` (B56), `voteCount` (B59) and the card-aging trio
 * (B60) — and the Card-Front Projection contract chunk widens it twice more,
 * with `stickers` (`FR-CARD-032`, `FE-58.1`) and `watching`
 * (`FR-COLLAB-022`/`FR-BOARD-030` AC4, `FE-29.1`/`FE-25.1`). Corrected in
 * place rather than deleted, because the sentence records a real claim that
 * was true when it was written: `FR-VIEW-007`'s enumeration closed, and the
 * later widenings each come from a **different** requirement naming the card
 * front. "Final" here should be read as "final for `FR-VIEW-007`", and the
 * next block to widen this shape should expect to correct this paragraph
 * again rather than to find a closed list.
 *
 * ⚠ **`watching` is the SECOND per-viewer field here, not the first, and that
 * ordering is the whole reason it is admissible.** `voteCount` (B59) was the
 * first: it is `null` for any caller who may not see tallies, and it landed
 * *after* B29 refused `watching` on the ground that the board face carries no
 * per-viewer field. See `watching`'s own docstring for the argument in full.
 *
 * The module header's claim that this shape **restates none of** the contracts
 * above it survives both additions: `stickers` is `./sticker`'s
 * `placedStickerSchema` whole and unnarrowed, and `watching` is a bare
 * nullable boolean rather than a second copy of `./watch`'s
 * `watchStateSchema` — that shape's `targetType`/`targetId` exist to identify
 * an answer in flight, and on this payload the card already identifies it.
 */
export const boardFaceCardSchema = cardSchema.extend({
  ...cardAgingFieldsSchema.shape,
  /**
   * The labels this card carries, in the board's label set order —
   * `FR-CARD-009`.
   *
   * `cardLabelSchema` from `./label`: three fields (`id`, `name`, `colour`)
   * rather than the whole `labelSchema`. This is the one response in the
   * product that scales with the size of the board — 200 cards × up to a dozen
   * labels each — and `position`, `boardId` and the two timestamps are all
   * recoverable from `boardContentsSchema.labels`, which rides on the same
   * payload. `cardListRefSchema` in `./card-detail` is the precedent for
   * narrowing a nested reference, and the argument is stronger here.
   *
   * ⚠ **Empty is the ordinary state, not a degenerate one**, and more so than
   * for any other array in this file: boards created before B15 have no labels
   * at all (there is no backfill), so `labels: []` on every card of a board is
   * the state of the majority of rows in the database. The array is never
   * omitted; a client must not read absence and emptiness as the same thing,
   * because only one of them can happen.
   */
  labels: z.array(cardLabelSchema),
  /**
   * `cards.start_at` — `FR-CARD-013`'s card-front badge. `null` means the
   * card has no start date, which is the ordinary state: most cards carry
   * neither date. Same serialisation note as `cardDetailSchema.startAt` —
   * Postgres' own spelling, never `z.iso.datetime()`; see that field for the
   * argument in full.
   */
  startAt: z.string().min(1).nullable(),
  /**
   * `cards.due_at` — `FR-CARD-013`'s card-front badge, and the field
   * `FR-CARD-015`'s five-state due-date badge (a DIFFERENT, later
   * requirement, unbuilt) will eventually derive its colour from. `null`
   * means the card has no due date.
   */
  dueAt: z.string().min(1).nullable(),
  /**
   * `cards.due_all_day boolean NOT NULL DEFAULT false` — `FR-EXP-029` edge
   * case (BE-44.7). Mirrors `cardDetailSchema.dueAllDay` on the board face
   * so the card-front badge can render date-only dues without a day shift.
   */
  dueAllDay: z.boolean(),
  /**
   * `cards.completed boolean NOT NULL DEFAULT false` — `FR-CARD-004`, B24
   * stage 2. Unlike `startAt`/`dueAt`, not nullable: every card has an
   * answer, so there is no third state to represent.
   *
   * ⚠ **Independent of the two dates beside it.** `FR-CARD-004`'s own
   * sentence: "Completion is independent of due dates — a card with no due
   * date can be marked complete." `completed: true` with `dueAt: null` is an
   * ordinary combination, not one this shape or its route should treat as
   * inconsistent.
   */
  completed: z.boolean(),
  /**
   * `cards.description IS NOT NULL` — `FR-BOARD-030`'s description badge,
   * B25 stage 1.
   *
   * A derived boolean, never the description text itself: the board face is
   * the one response that scales with the size of the board, and shipping a
   * card's whole description on it (up to 16 KB, `CARD_DESCRIPTION_MAX_
   * LENGTH` in `./card-detail`) on every board open is exactly what
   * `cardDetailSchema` exists to avoid. Correct **only** because
   * `PATCH /cards/:id/description` normalises an empty-after-trim submission
   * to `NULL` — see `./card-detail.ts`'s `description` field — so `IS NOT
   * NULL` is a true predicate for "has a non-empty description" rather than
   * a stand-in that also fires on `''`.
   */
  hasDescription: z.boolean(),
  /**
   * How many comments this card carries — `FR-BOARD-030`'s comment-count
   * badge, B25 stage 1.
   *
   * ⚠ **A count, and deliberately not the comments themselves.**
   * `GET /cards/:id/comments` (`./comment`) is a keyset stream with no
   * total on purpose — `FR-COLLAB-001`'s shape, comments being the one
   * collection in the product with no upper bound — so this field is the
   * *only* place a comment total exists on the wire, and it exists because
   * the badge strip needs a number, not a list.
   */
  commentCount: z.number().int().nonnegative(),
  /**
   * The people on this card — `FR-BOARD-030`'s member-avatars badge, B25
   * stage 1. `FR-BOARD-030`'s own behaviour text names "member avatars
   * aligned to the right" inside the badge-strip sentence, so this is ruled
   * in rather than assumed.
   *
   * ⚠ **`publicProfileSchema`, not `cardMemberSchema`.** See the module
   * header: the board face needs a face and a name, not a join date, so it
   * reuses the profile shape directly rather than declaring a fourth member
   * view.
   *
   * ⚠ **Empty is the ordinary state, not a degenerate one.** `FR-CARD-007`'s
   * edge case — "a card may have zero members; there is no implicit
   * assignee" — holds here exactly as it holds on the card back. The array
   * is never omitted.
   *
   * Ordered identically to `cardDetailSchema.members` — `card_members.
   * joined_at, users.id` — so a member avatar row does not reshuffle between
   * the board face and the card back for a card nobody changed.
   */
  members: z.array(publicProfileSchema),
  /**
   * This card's cover, or `null` for no cover — `FR-CARD-021`,
   * `FR-BOARD-031`, B25 stage 1.
   *
   * `null` is the state of **every** card in the product today: `BE-25.1`
   * is a read, and no route anywhere yet writes a cover (`BE-25.2`). See
   * `./card-cover.ts`'s header for the shape and why it carries no `type`,
   * no palette enum and no URL of any kind.
   */
  cover: cardCoverSchema.nullable(),
  /**
   * How many checklist items this card carries **in total, across every
   * checklist on it** — the denominator of `FR-CARD-018`'s card-front
   * badge, B26.
   *
   * AC4 is the whole specification and it is literal: "given a card with
   * two checklists totalling 7 items of which 3 are checked, when the board
   * is viewed, then the card front shows a checklist badge of **3/7**." So
   * this is `7` — summed across the card's checklists, never per checklist,
   * and there is deliberately **no** per-checklist breakdown on the board
   * face and no `checklistCount` beside it. A client that wants either
   * opens the card back, where `checklists` carries every checklist and
   * every item.
   *
   * ---------------------------------------------------------------------
   * ⚠ ZERO IS SHIPPED, NEVER OMITTED — AND THAT IS NOT A CONTRADICTION OF
   * `BE-25.1`'S "BADGES WHOSE SOURCES DO NOT EXIST YET ARE OMITTED, NOT
   * ZEROED"
   * ---------------------------------------------------------------------
   * That rule is about a badge whose **source is unmodelled**: attachments
   * (`BE-27.x`), custom fields (`BE-56.x`) and votes (`BE-59.x`) have no
   * route, no contract and no read, so a `0` for them would be a claim the
   * server cannot make — it would mean "this card has no attachments" when
   * the truth is "nobody has built attachments". Checklists have a table, a
   * contract (`./checklist`), eleven routes and a read; a card with none is
   * a card that genuinely has none, and `0` is the honest answer rather
   * than a placeholder for one.
   *
   * Both fields are therefore **required and non-nullable**, seeded at `0`
   * for every card on the face, exactly as `commentCount` is. Optional
   * would let `undefined` through and cost the compile-time net that makes
   * a missing projection a type error in the route's mapper.
   *
   * ⚠ **`0/0` does not distinguish "this card has no checklists" from
   * "this card has one checklist with no items", and that is deliberate.**
   * `FR-CARD-018`'s badge is `checked/total`; both states render as no
   * badge at all, so nothing on the card front can tell them apart and a
   * field that could would be a third number nothing renders. Do not
   * "complete" this pair with a `checklistCount` — the card back already
   * answers that question for the one surface that asks it.
   */
  checklistItemCount: z.number().int().nonnegative(),
  /**
   * How many of those items are checked — the numerator of `FR-CARD-018`'s
   * badge (`3` in AC4's `3/7`), summed across every checklist on the card.
   *
   * ⚠ **Never greater than `checklistItemCount`**, and the two come from
   * **one** grouped statement in the route rather than two, precisely so
   * that a pair that disagrees is unrepresentable rather than merely
   * untested. Nothing in this schema can express the relation, which is why
   * the route states it: `z.number()` cannot say "at most that other
   * field", and a badge reading `5/3` is the kind of wrong nobody reports.
   *
   * ⚠ **It counts ticked items, and it has nothing to do with
   * `completed` five fields up.** `FR-CARD-017`'s edge case keeps them
   * apart in as many words — "checking an item is not the same as
   * completing the card; the two are independent (see `FR-CARD-004`)" — so
   * `completed: true` with `checklistCheckedItemCount: 0` and the reverse
   * are both ordinary states.
   *
   * ⚠ **Unaffected by `checklistSchema.hideChecked`.** That preference is
   * per user per checklist and hides checked items from a *view*; a badge
   * that shrank when a member hid what they had finished would report a
   * different `3/7` to two members of one board. The board face reads no
   * preference row at all — `hideChecked` reaches the wire on the card
   * back and nowhere else.
   */
  checklistCheckedItemCount: z.number().int().nonnegative(),
  /**
   * How many attachments this card carries — `FR-CARD-022` AC4's card-front
   * badge ("given a card has attachments, when the board is viewed, then the
   * card front shows an attachment count badge") and the sixth and LAST of
   * `FR-VIEW-007`'s six named badges to land on this shape. B27.
   *
   * ---------------------------------------------------------------------
   * ⚠⚠ BOTH KINDS COUNT — FILE AND LINK — AND THAT IS A DECISION, NOT AN
   * OMISSION OF A `WHERE kind = 'file'`
   * ---------------------------------------------------------------------
   * AC4 sits inside `FR-CARD-022`, the file-upload requirement, which could
   * be misread as scoping the badge to files alone. It is not read that way
   * here, for three reasons that all point the same way:
   *
   *   1. `FR-CARD-023` puts a pasted or picked LINK in the identical
   *      "attachments section" a file lands in — "the result is stored as a
   *      link attachment" — never a second, separately-badged collection.
   *   2. `FR-CARD-024` is the requirement that actually OWNS the badge's
   *      *behaviour* (its edge case: "the attachment count badge on the card
   *      front must decrement immediately after deletion"), and it treats
   *      every attachment uniformly — one preview/rename/download/delete
   *      surface, one ordering rule ("most-recent-first"), no kind-scoped
   *      clause anywhere in it.
   *   3. A member who pastes a cloud-storage link and watches the badge NOT
   *      move would read that as a bug, not a feature — `attachments.kind`
   *      distinguishes how a row is FETCHED (a download vs. a redirect),
   *      never whether it counts toward this number.
   *
   * There is deliberately no `WHERE kind = 'file'` in the statement that
   * fills this field.
   *
   * **It counts comment-attached FILES too — automatically, not by a special
   * case.** `POST /comments/:id/attachments` with `kind: 'file'` inserts an
   * ordinary `attachments` row carrying this card's `card_id`
   * (`FR-COLLAB-005`'s own text: "the file also joins the card's attachment
   * section"), so it is indistinguishable at read time from a file attached
   * directly and is summed the same way. A `card_link`/`board_link` comment
   * attachment writes to `comment_attachments`, a different table this field
   * never queries, so it correctly does NOT move this badge.
   *
   * **It does NOT count related cards.** `FR-TMPL-028`'s related-card chips
   * are `card_relations` rows — a different table, with no `attachments` row
   * of their own and no `card_id` this statement could group on. Product
   * prose calling a related card "an attachment" is
   * `packages/shared/src/attachment.ts`'s own recorded trap, not a licence
   * to fold `relatedCards` into this count.
   *
   * ⚠ **`0` is shipped, never omitted — and the rule that used to withhold
   * this field no longer applies to it.** `BE-25.1`'s "badges whose sources
   * do not exist yet are omitted, not zeroed" governed attachments through
   * B26, alongside custom fields (`BE-56.x`) and votes (`BE-59.x`), because
   * none of the three had a table, a contract or a read — a `0` for any of
   * them would claim "this card has none" when the truth was "nobody built
   * it". B27 gives attachments all three, so a card with none genuinely has
   * none and `0` is the honest answer, `checklistItemCount`'s own precedent
   * landed one badge later. Required and non-nullable, seeded at `0` for
   * every card on the face — `commentCount`'s footing exactly.
   */
  attachmentCount: z.number().int().nonnegative(),
  customFieldBadges: z.array(cardCustomFieldBadgeSchema),
  /**
   * The vote badge count for the card front. `null` represents hidden tallies.
   */
  voteCount: z.number().int().nonnegative().nullable(),
  /** PR-list approvals badge; `null` unless the card sits in the board's PR list. */
  approvals: cardFrontApprovalsSchema.nullable(),
  /**
   * The stickers placed on this card's **front** — `FR-CARD-032`, and the
   * projection `FE-58.1`'s residual names ("`GET /boards/:id/contents`
   * projects no placed stickers").
   *
   * ⚠ **`placedStickerSchema` WHOLE AND UNNARROWED, WHICH IS THE OPPOSITE OF
   * `cardLabelSchema`'S CALL TWELVE FIELDS UP — AND DELIBERATELY SO.**
   * `labels` narrows because the board face is the one response that scales
   * with the size of the board and a card can carry a dozen labels (200 cards
   * × 12 = 2,400 objects). A sticker has no such fan-out: it is Parity-Later
   * decoration and the ordinary card carries **zero**. The argument that
   * decides it runs the other way — `cardDetailSchema.stickers`
   * (`placedStickerListSchema`, the same element) is what the card back
   * reads, so one shape means the card front and the card back **cannot
   * disagree about a placement**, which is exactly `FR-CARD-032` AC4's
   * observable: "another member opening the board sees it in the same
   * position". A narrowed card-front view would make that agreement a
   * property of two mappers staying in step rather than of the type.
   *
   * ⚠ **`surface = 'front'` ONLY, and the database is what makes that a
   * closed choice rather than an open string test.** `card_stickers.surface`
   * carries `ck_card_stickers_surface CHECK (surface IN ('front','back'))`,
   * so there are exactly two values and a back sticker is simply not
   * card-front information. The card back keeps reading **both** surfaces
   * through `readPlacedStickers`, unchanged — this field is not a
   * replacement for that read and must not be treated as one. The element
   * still carries its own `surface` field (it is `placedStickerSchema`
   * whole), and on this array it is always `'front'`; that redundancy is the
   * price of the one-shape guarantee above and is worth it.
   *
   * ⚠ **Empty is the ordinary state, not a degenerate one**, on `labels`'
   * and `members`' exact footing and more strongly than either: stickers are
   * decoration, so `[]` is the state of very nearly every card in the
   * database. The array is **required and never omitted** — a client must
   * not read absence and emptiness as the same thing, because only one of
   * them can happen.
   *
   * Ordered `z_index, id` — `FR-CARD-032`'s stacking order is adjustable
   * after placement, so the array's order *is* the stacking order and a
   * client renders it back to front without sorting.
   */
  stickers: z.array(placedStickerSchema),
  /**
   * Whether **this viewer** watches this card — `FR-COLLAB-022`,
   * `FR-BOARD-030` AC4, and the field `FE-29.1`/`FE-25.1` have been waiting
   * on.
   *
   * ---------------------------------------------------------------------
   * ⚠⚠ THIS FIELD WAS REFUSED BY NAME IN `./watch.ts` AND IN
   * `watch.test.ts`, AND THE REFUSAL IS RELEASED HERE ON EVIDENCE RATHER
   * THAN OVERRIDDEN
   * ---------------------------------------------------------------------
   * B29's argument was: "a watch is invisible to other members by
   * requirement, so it is not board-face information", with the product
   * tie-breaker settling it before the anonymous-caller argument had to.
   * Two facts say that reading is wrong, and both are in the corpus rather
   * than in taste:
   *
   *   1. **`FR-BOARD-030` puts the eye on the card front in its own
   *      behaviour text and in AC4.** Behaviour: "a compact strip of badges
   *      … a **watch (eye) indicator when the viewer is watching the card**,
   *      and member avatars aligned to the right." AC4: "Given a card the
   *      viewer watches, when it renders, then an eye indicator is shown
   *      **to that viewer only**." `FR-COLLAB-022` agrees in as many words:
   *      "An eye icon on the card back **(and on the card front)**
   *      indicates the watching state." *Invisible to other members* means
   *      **per-viewer**, not **absent** — B29 read the first as the second,
   *      and no requirement supports that step.
   *   2. **A per-viewer, viewer-gated, nullable field already ships on this
   *      exact shape.** `voteCount` three fields up is `null` for any caller
   *      who may not see tallies, including every anonymous one. B59 landed
   *      after B29, so the categorical claim that the board face carries no
   *      per-viewer field was already false when the residual was written.
   *
   * The `cardDetailSchema` half of `./watch.ts`'s refusal **still stands**
   * and is deliberately not touched: `GET /cards/:id/watch` ships, the card
   * back's second request works today, and nothing there is blocked.
   *
   * ⚠ **`null` means "there is no *you*", and it is the direct answer to
   * `./watch.ts`'s own stated objection** ("`null` would put a third state
   * on a boolean `FR-COLLAB-022` gives two"). On the card back that
   * objection stood because a second request was available and cheap. On the
   * board face there is no second request available at all — `CardTile` and
   * `CardBadges` receive board-face data only through the `card` prop — so
   * the choice is a three-state field or no answer. `voteCount` already
   * established the three-state per-viewer convention on this shape, and
   * `null` here means exactly what it means there: the server declines to
   * answer for this caller, rather than answering `false`. A caller with no
   * session gets `null`; `false` would assert that somebody who does not
   * exist is not watching.
   *
   * ⚠ **CARD-SCOPED `watches` ROWS ONLY — a list watch or a board watch does
   * NOT light this eye.** `ck_watches_target` guarantees exactly one of
   * `card_id`/`list_id`/`board_id` is non-null, and
   * `watchStateSchema.targetType`'s docstring already ruled the identical
   * question by name: it reports "the type of the target the route was
   * called on, NOT the arm that made `watching` true". Unioning the three
   * arms here would put an eye on every card of a watched board while the
   * card back's own toggle still read "Watch" — a visible contradiction with
   * nothing failing.
   *
   * ⚠ **`GET /boards/:id/contents` STAYS ANONYMOUS-CAPABLE.** This field
   * must not acquire a `requireUser` on the route to make itself
   * non-nullable: that would turn a public board's read into a 401 oracle,
   * which is the rule a caller outside a read scope gets 404 and never 401.
   * `null` exists so the route does not have to change its auth.
   *
   * ⚠⚠ **`null` IS NOT "ANONYMOUS" EVERYWHERE THIS FIELD IS SERIALISED, AND
   * THE DIVERGENCE IS RECORDED HERE RATHER THAN LEFT FOR A READER TO
   * DISCOVER.** `cardMirrorSchema` extends this shape, so `watching` also
   * rides `POST /cards/:id/mirrors` and `GET /card-mirrors/:id`
   * (`apps/server/src/routes/card-mirrors.ts`, `toMirror`) — and those two
   * answer `null` to a **signed-in** caller, because the mapper is handed no
   * `userId` and threading one through two private functions in an unowned
   * file to answer a per-viewer question on a single-mirror route was a
   * larger edit than the honest `null`. `voteCount` beside it does exactly
   * the same thing on exactly those routes, for the same reason, and did so
   * first.
   *
   * So read the paragraph above precisely: `null` is **"no viewer answer is
   * available on this route"**, of which "there is no *you*" is the
   * board-face case and the only case that surface can produce. A client that
   * needs a mirror's watch state asks `GET /cards/:id/watch` about the
   * **source** card, which ships. Closing the gap is a card-mirrors change
   * and belongs to whoever owns that file.
   */
  watching: z.boolean().nullable(),
});

export type BoardFaceCard = z.infer<typeof boardFaceCardSchema>;

export const cardMirrorSourceBoardSchema = z.object({
  id: ulidSchema,
  title: z.string().min(1),
});

export type CardMirrorSourceBoard = z.infer<typeof cardMirrorSourceBoardSchema>;

export const cardMirrorSchema = boardFaceCardSchema.extend({
  surfaceType: z.literal('mirror'),
  isLinkCard: z.literal(false),
  linkTargetUrl: z.null(),
  linkTargetTitleCache: z.null(),
  sourceCardId: ulidSchema,
  sourceBoard: cardMirrorSourceBoardSchema,
  sourceArchived: z.boolean(),
  sourceDeleted: z.boolean(),
  createdByUserId: ulidSchema.nullable(),
});

export type CardMirror = z.infer<typeof cardMirrorSchema>;

export const boardFaceOrdinaryCardSurfaceSchema = boardFaceCardSchema.extend({
  surfaceType: z.literal('card'),
  isLinkCard: z.literal(false),
  linkTargetUrl: z.null(),
  linkTargetTitleCache: z.null(),
});

export type BoardFaceOrdinaryCardSurface = z.infer<
  typeof boardFaceOrdinaryCardSurfaceSchema
>;

export const boardFaceLinkCardSurfaceSchema = boardFaceCardSchema.extend({
  surfaceType: z.literal('link_card'),
  isLinkCard: z.literal(true),
  linkTargetUrl: z.string().min(1).max(LINK_CARD_TARGET_URL_MAX_LENGTH),
  linkTargetTitleCache: z
    .string()
    .min(1)
    .max(LINK_CARD_TARGET_TITLE_CACHE_MAX_LENGTH)
    .nullable(),
  linkTargetBoardTitleCache: z
    .string()
    .min(1)
    .max(LINK_CARD_TARGET_BOARD_TITLE_CACHE_MAX_LENGTH)
    .nullable()
    .optional(),
  linkTargetUnavailable: z.boolean(),
});

export type BoardFaceLinkCardSurface = z.infer<typeof boardFaceLinkCardSurfaceSchema>;

export const boardFaceCardSurfaceSchema = z.discriminatedUnion('surfaceType', [
  boardFaceOrdinaryCardSurfaceSchema,
  cardMirrorSchema,
  boardFaceLinkCardSurfaceSchema,
]);

export type BoardFaceCardSurface = z.infer<typeof boardFaceCardSurfaceSchema>;

const boardFaceListCardSchema = z.union([
  boardFaceCardSurfaceSchema,
  boardFaceCardSchema,
]);

/**
 * One column of the board face: a list, exactly as B11 serves it, plus the
 * cards it is holding.
 *
 * `listSchema.extend()` and never a fresh `z.object()` — the header argues the
 * general case; the specific one is that `cardCount` below reads its meaning
 * from `listSchema`'s docstring and would silently acquire a *second* meaning
 * if this file redeclared the field.
 */
export const boardFaceListSchema = listSchema.extend({
  /**
   * How many of this list's cards are on the board face — live, unarchived, in
   * a list that is itself unarchived. Always `cards.length` below.
   *
   * ---------------------------------------------------------------------------
   * ⚠ IT IS ADDED BESIDE `cardCount`, AND IT NEVER REDEFINES IT
   * ---------------------------------------------------------------------------
   * `listSchema.cardCount` counts a list's cards with `deleted_at IS NULL`,
   * **archived and open alike**, because `FR-BOARD-016`'s delete confirmation
   * must state "that the list and all of its cards — open and archived alike —
   * are deleted irreversibly", and a client cannot state a number it was never
   * given. `listSchema`'s own docstring, `apps/server/src/routes/lists.ts`'s
   * `CARD_COUNT` and `apps/server/README.md` §13 all hand the board face's
   * different number forward by name, and all three say the same thing: the
   * board-face block adds a field **beside** `cardCount` rather than
   * redefining it, because redefining it would leave `FR-BOARD-016`'s sentence
   * with nothing to read. This is that field.
   *
   * So the two disagree on any list holding an archived card, and that is the
   * point of having both: `cardCount: 2, openCardCount: 1` is the honest
   * description of a list with one open card and one archived one.
   *
   * ---------------------------------------------------------------------------
   * WHY IT IS NOT REDUNDANT WITH `cards.length`, WHICH IT EQUALS TODAY
   * ---------------------------------------------------------------------------
   * It is `cards.length` on every response this route can produce, and a later
   * reader will be tempted to delete it as duplication. `FR-BOARD-035` /
   * `BE-47.4` (V1) is why not: 5,000 open cards per board is a real limit with
   * a warning threshold at 4,500, and the block that enforces it is the block
   * that will stop this route from serving every card in a 5,000-card list.
   * When that happens `cards` becomes a page and this count stays whole — the
   * badge on a collapsed or truncated column is a *count*, not a length. Adding
   * the field now costs one integer and keeps that change from being a wire
   * break.
   *
   * ---------------------------------------------------------------------------
   * ⚠⚠ B19 — IT SHRINKS UNDER A FILTER, WHICH IS THE OPPOSITE OF THE CASE
   * ARGUED DIRECTLY ABOVE, AND BOTH ARE RIGHT
   * ---------------------------------------------------------------------------
   * `GET /boards/:id/contents` takes `?members=`, `?labels=` (B19,
   * `FR-SEARCH-002`, `FR-SEARCH-003`, `FR-SEARCH-008`) and, since B24,
   * `?due=`/`?completion=` (`BE-24.5`, `FR-SEARCH-004`, `FR-SEARCH-005`)
   * facets — `packages/shared/src/board-filter.ts`. Under any one of the four,
   * this field is the **filtered** array's length — a list of ten cards of
   * which two match a filter answers `openCardCount: 2, cards: [two]` — so the
   * `openCardCount === cards.length` invariant the route establishes by
   * construction holds unchanged, and a client needs no second rule for the
   * filtered case.
   *
   * That reads as a contradiction of the paragraph above, where the whole
   * justification for the field's existence is that `cards` will one day be
   * truncated and **this count must stay whole**. The two are distinguished by
   * what each does to the question being asked, and the distinction is the
   * thing to carry:
   *
   *   - **Truncation (`FR-BOARD-035`/`BE-47.4`) is a TRANSPORT concern.** The
   *     card is on the board face; the response merely did not carry it. The
   *     honest badge is the whole number, and a client showing `cards.length`
   *     there would be under-reporting the board.
   *   - **A filter changes what "on the board face" MEANS for this request.**
   *     `FR-SEARCH-002`'s filter "hides non-matching cards"; a hidden card is
   *     not on the face the caller asked for, so a count that included it would
   *     be answering a question nobody asked — and `FR-SEARCH-008` AC4/AC5 make
   *     the visible set the observable ("the visible card set grows or stays the
   *     same" / "shrinks or stays the same"), which is what a filter badge has
   *     to be counting.
   *
   * ⚠ **`cardCount` beside it is NOT filtered**, and the two therefore diverge
   * far more widely under a filter than they do under an archive. It counts a
   * list's cards open and archived alike because `FR-BOARD-016`'s delete
   * confirmation reads it, and that sentence is about the list, not about the
   * request — narrowing it would make a delete confirmation understate what the
   * user is about to lose, which is the one number in this shape where being
   * wrong is destructive.
   *
   * ⚠ When both a filter and truncation are eventually live at once, this field
   * is the filtered whole and `cards` is a page of it. The block that lands
   * `BE-47.4` should read both halves of this docstring rather than either.
   */
  openCardCount: z.number().int().nonnegative(),
  /**
   * This list's cards, in list order — `ORDER BY position, id`, the server's
   * order, rendered as received.
   *
   * `cardSchema` from `./card` unchanged: the board face and
   * `GET /lists/:id/cards` return the *same* card, so a client that has dragged
   * a card (`POST /cards/:id/move`, which answers with one card) can splice the
   * response straight into the array it already holds. A narrower
   * `CardFront` shape for this route would be a second card view kept identical
   * to the first by hand, and the first divergence would show up as a field
   * missing from an optimistic update.
   *
   * ⚠ **B15 CHANGED THE ELEMENT TYPE AND THE SPLICE IS THE PRICE. CORRECTED IN
   * PLACE, BECAUSE THIS IS THE PARAGRAPH A CLIENT AUTHOR WILL BE READING WHEN
   * IT BITES.** This array is now `boardFaceCardSchema` — `cardSchema` plus
   * `labels` — so the two shapes are no longer identical and a raw splice of
   * `POST /cards/:id/move`'s answer drops the labels off the card it replaces.
   * The array element is still `cardSchema`-*derived* rather than a
   * hand-maintained second view, which is what keeps the divergence to exactly
   * one known field instead of an open-ended drift: a client splicing a moved
   * card must carry its existing `labels` across, and nothing else. That is a
   * real cost of not widening `cardSchema`, it was weighed against giving all
   * four B12 routes a label read, and the header records both sides. A later
   * badge block that widens `cardSchema` instead would restore the identity.
   *
   * ⚠ **Empty is a legitimate, expected value** — `FR-VIEW-007` AC3: "given a
   * list holds no cards, when the board renders, then the list is still shown
   * with an add-card affordance". The list is present with `cards: []`; it is
   * never omitted, and a client must not read absence and emptiness as the same
   * thing, because only one of them can happen.
   *
   * ⚠ **A card whose parent list is archived is absent from this array, while
   * its own `archived` reads `false`** — and that is correct on both counts.
   * `FR-BOARD-015` makes archiving a list distinct from archiving its cards, so
   * `POST /lists/:id/archive` writes one column on one row and the *list's* flag
   * is what takes its cards off the board face. The card is honest about itself;
   * the query is what knows about its list. See the route.
   */
  cards: z.array(boardFaceListCardSchema),
});

export type BoardFaceList = z.infer<typeof boardFaceListSchema>;

/**
 * `GET /boards/:id/contents` — the whole board face in one response.
 *
 * `{ board, labels, lists }` — the lists in board order (`ORDER BY position,
 * id`), each one's cards in list order, and (since B15) the board's whole label
 * set, which is the ordering authority for every card's `labels` array and the
 * source `FE-15.1`'s picker is drawn from. This is `FR-VIEW-007` AC1 — "given a board
 * with five lists, when a member opens it, then the five lists render left to
 * right in the board's stored order" — with AC2's "other members see the same
 * order" resting on the same server-side ordering.
 *
 * ---------------------------------------------------------------------------
 * WHY THE BOARD IS NESTED RATHER THAN ASSUMED
 * ---------------------------------------------------------------------------
 * A client opening a board URL needs the board's title, address, closed state
 * and visibility to render the header before it can render anything else, and
 * `boardSchema` is what carries them. Answering with a bare `List[]` would make
 * every board-face client issue `GET /boards/:id` alongside this — two requests
 * for one screen, whose two answers can disagree about whether the board is
 * still open. The board row also has to be read here regardless, because it is
 * where the permission decision is made; nesting it costs nothing and removes a
 * round trip.
 *
 * ⚠ **A closed board answers `200` with `closed: true`, not `409`.**
 * `FR-EXP-012` AC2 requires a closed board's URL to resolve to a read-only
 * state carrying a Reopen action rather than to a not-found page, and
 * `GET /boards/:id` already behaves this way (`closed_at` is deliberately
 * absent from its `WHERE`). It is the *mutations* that refuse a closed board
 * with 409, and pattern-matching on them here would break AC2 — see the route.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS SHAPE DOES NOT CARRY
 * ---------------------------------------------------------------------------
 *   - **Archived lists and archived cards.** `FR-VIEW-007`'s edge case: "Archived
 *     lists and archived cards are absent from the board face; they are reached
 *     through the archive." So there is no `?state` parameter and no archived
 *     branch of this shape at all — the archived-items surface is `FR-EXP-010`,
 *     owned by `BE-22.2`, and it is a mixed list of cards *and* lists that this
 *     shape could not express anyway.
 *   - **The board's members.** `GET /boards/:id/members` (B09/B10) answers that,
 *     and `FR-VIEW-007` names nothing of it. A member array here would be
 *     profile rows on a payload every board open pays for.
 *   - **Anything about the *other* views.** `FR-VIEW-007` is the board face —
 *     the one view with columns. Table, calendar, timeline and map are
 *     `FR-VIEW-*`'s and `B40`'s, which is also why this route is `/contents` and
 *     not `/view`: `view` is a namespace those blocks will want, and taking it
 *     for the board face would make the first of them either rename this route
 *     or nest under a word that already means something else.
 */
export const boardContentsBoardSchema = boardSchema.extend({
  background: boardBackgroundSchema,
  cardAgingMode: cardAgingModeSchema,
  /**
   * `boards.covers_enabled` — `FR-CARD-025` AC5 and `FR-BOARD-031`, and the
   * whole of `FE-25.2`'s residual ("`boards.covers_enabled` has no read/write
   * route at all").
   *
   * The column has existed since `0002_boards_lists_cards.sql`
   * (`boolean NOT NULL DEFAULT true`) and **no route has ever read or written
   * it**. `PATCH /boards/:id/cover-settings` (`./card-cover`'s
   * `boardCoverSettingsSchema`) is the writer; this is the projection the
   * board face needs, on `cardAgingMode`'s exact footing directly above —
   * board-level *render* information, on a copy of `boardSchema` that
   * serialises exactly **one** route (`GET /boards/:id/contents`), never on
   * `boardSchema` itself, which serialises `GET /boards/:id`, `POST /boards`
   * and every board patch.
   *
   * ---------------------------------------------------------------------
   * ⚠⚠ THE SERVER PROJECTS THIS FLAG AND FILTERS NOTHING. SUPPRESSION IS THE
   * CLIENT'S ACT
   * ---------------------------------------------------------------------
   * `FR-CARD-025` AC5 reads "given the board setting for covers is disabled,
   * when the board is viewed, then card fronts show **no covers** even for
   * cards that have one set" — a statement about what is *rendered*, and it
   * is tempting to serve it by nulling `boardFaceCardSchema.cover` when this
   * is `false`. **Do not.**
   * `apps/server/src/routes/board-contents.integration.test.ts` already ruled
   * on it by name: *"covers_enabled=false does not suppress the cover field
   * or any other badge — project the fact, filter nothing"*, which is B15's
   * colourless-labels ruling one table over. A server-side filter would make
   * a card's stored cover unreadable to the very surface that has to offer
   * "re-enable covers", and would make re-enabling the setting a change the
   * client could not render without a second full board read.
   *
   * ⚠ **Required and non-nullable.** Every board has an answer — the column
   * is `NOT NULL DEFAULT true` — so there is no third state, and `.optional()`
   * would let `undefined` through and cost the compile-time net that makes a
   * missing projection a type error in the route's mapper
   * (`checklistItemCount`'s stated argument, applied here).
   */
  coversEnabled: z.boolean(),
  /**
   * `boards.show_card_numbers_on_front` — `FR-CARD-005`'s optional board
   * setting, written by `PATCH /boards/:id/card-numbers-on-front`
   * (`./board`'s `boardCardNumbersSettingSchema`). On `coversEnabled`'s exact
   * footing directly above: render information for the board face, on this
   * copy that serialises one route, never on `boardSchema`.
   *
   * ⚠ Projected, and nothing is filtered: every card still carries its `ref`
   * whatever this says. The card front shows `ref` only when this is `true`
   * AND `ref` is non-null — a viewer outside the reference scope sees no
   * number (`docs/notes/card-keys.md`).
   */
  showCardNumbersOnFront: z.boolean(),
  /**
   * `boards.required_approvals` (1..20) — approvals a PR-list card needs to be
   * "met". Written by `PATCH /boards/:id/required-approvals`.
   */
  requiredApprovals: z.number().int().min(1).max(20),
  /**
   * `boards.dangerous_comments` (1..10000, or `null` = off) — a card whose
   * `commentCount` reaches this takes a reddish background on the front.
   * Written by `PATCH /boards/:id/dangerous-comments`.
   */
  dangerousComments: z.number().int().min(1).max(10000).nullable(),
  /**
   * Whether **this viewer** can open a `KEY-NNNN` reference on this board —
   * `docs/notes/card-keys.md`, "Resolution". `true` for a signed-in reader
   * through membership or workspace/Enterprise visibility; `false` for an
   * anonymous reader and for one who reads the board only because it is
   * public, because a reference is enumerable where a short link is not.
   *
   * The board face builds card paths itself for its calendar, timeline, map
   * and table views (their rows carry no `path`), and a path it builds must
   * resolve for the person who clicks it and must match the path the server
   * built for the same card byte for byte (`FR-BOARD-023`'s card-return state
   * is keyed on it). So the client passes `board.cardKey` to `cardRefFor` only
   * when this is `true`, and `null` otherwise — exactly the server's rule.
   *
   * A per-viewer field on this copy of `boardSchema`, never on `boardSchema`
   * itself, which serialises board mutations whose callers are always members.
   */
  cardRefsResolve: z.boolean(),
});

export type BoardContentsBoard = z.infer<typeof boardContentsBoardSchema>;

export const boardContentsSchema = z.object({
  /**
   * `boardSchema` from `./board`, unchanged and not extended — the same object
   * `GET /boards/:id` returns, byte for byte, so a client can hold one board
   * record whichever route filled it.
   */
  board: boardContentsBoardSchema,
  /**
   * The board's **whole** label set, in set order (`ORDER BY position, id`) —
   * added by B15 (`FR-CARD-010`).
   *
   * ---------------------------------------------------------------------------
   * ⚠ A SIBLING OF `board` AND `lists`, NEVER A FIELD ON `boardSchema`
   * ---------------------------------------------------------------------------
   * `boardSchema` serialises `GET /boards/:id`, `POST /boards` and every board
   * patch; putting the label set there would ship it on every board rename.
   * That is the `cardSchema` argument one level up, and it is the reason this
   * shape composes `boardSchema` unchanged.
   *
   * ---------------------------------------------------------------------------
   * ⚠ THE WHOLE SET, NOT THE UNION OF THE LABELS THE CARDS CARRY
   * ---------------------------------------------------------------------------
   * Deriving it from the card query is one statement fewer and passes every
   * assertion on a board where every label is in use — which is every board a
   * test constructs by applying labels. It breaks two things silently:
   * `FE-15.1`'s picker must list the board's labels **including the ones no
   * card carries**, and `FR-CARD-010` AC1's "appears in the board's label set
   * and is immediately applicable" is a claim about a label nothing yet uses.
   *
   * Three more reasons it belongs on this payload at all, in decreasing order
   * of force. `FR-CARD-009` AC2 orders each card's labels "in the board's label
   * set order", and AC1's *immediate* apply is an optimistic insert the client
   * performs before the server answers — the only thing that says where in the
   * array the new label goes is the set. `FR-CARD-010` AC4 ("open another
   * board → the first board's labels are not present in the second board's
   * label set") is an assertion about a set, which has to be observable per
   * board. And the cost is bounded — six rows on a new board, tens at most —
   * against a second round trip on every board open, which is the same trade
   * this shape made when it nested `boardSchema` whole.
   *
   * ⚠ **Empty is legitimate and is the state of every board that predates
   * B15.** `POST /boards` seeds six labels from B15 onwards
   * (`DEFAULT_BOARD_LABEL_COLOURS` in `./label`) and **nothing backfills the
   * others**, so `labels: []` is the ordinary answer for most boards in the
   * database rather than an edge case. It never means "you may not see this
   * board" — that is a 404, decided on the board statement before this array is
   * read.
   *
   * ⚠ **Unfiltered, colourless labels included.** The picker lists them and
   * `FR-CARD-010` creates them; a filtered set would contradict the per-card
   * arrays it is supposed to be the ordering authority for.
   */
  labels: labelListSchema,
  /**
   * The board's open lists, left to right.
   *
   * Empty on a board that has no lists yet, which is every board immediately
   * after `POST /boards` — the first-run state `FE-13.x`'s empty board renders,
   * and not an error.
   */
  lists: z.array(boardFaceListSchema),
});

export type BoardContents = z.infer<typeof boardContentsSchema>;
